import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import {
  Scale, ArrowLeftRight, Layers, FileSpreadsheet, Download, RefreshCw,
  AlertTriangle, CheckCircle2, Circle,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const TH = 'px-3 py-2 text-left text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
const THR = 'px-3 py-2 text-right text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
const TD = 'px-3 py-2 text-sm';
const TDR = 'px-3 py-2 text-sm text-right font-display';

// frontend-side CSV export
function exportCSV(filename, columns, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [columns.map((c) => esc(c.label)).join(',')]
    .concat(rows.map((r) => columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(',')))
    .join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const SummaryTile = ({ label, value, tone = 'default', testId }) => {
  const tones = { default: 'border-slate-200', amber: 'border-amber-300 bg-amber-50', rose: 'border-rose-300 bg-rose-50', green: 'border-emerald-300 bg-emerald-50' };
  return (
    <div className={`border rounded-sm px-4 py-2.5 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="font-display text-lg font-extrabold text-[#3b4872]">{value}</div>
    </div>
  );
};

const DirToggle = ({ options, value, onChange, testPrefix }) => (
  <div className="flex border border-slate-300 rounded-sm overflow-hidden">
    {options.map((o) => (
      <button key={o.id} onClick={() => onChange(o.id)}
        className={`px-3 py-1.5 text-xs font-display font-bold uppercase ${value === o.id ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
        data-testid={`${testPrefix}-${o.id}`}>
        {o.label}
      </button>
    ))}
  </div>
);

// ─── Retention tab ───────────────────────────────────────────────────────────
function RetentionTab({ projectId }) {
  const [dir, setDir] = useState('receivable');
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    apiGet('/finance/retention', { direction: dir, ...(projectId ? { project_id: projectId } : {}) })
      .then(setD).catch(() => toast.error('Failed to load retention ledger'));
  }, [dir, projectId]);

  const rows = d?.rows || [];
  const recvCols = [
    { key: 'project_name', label: 'Project' }, { key: 'client_name', label: 'Client' },
    { key: 'retention_deducted', label: 'Retention Deducted' }, { key: 'released_to_us', label: 'Released to Us' },
    { key: 'balance', label: 'Balance Receivable' }, { key: 'expected_release_date', label: 'Expected Release' },
  ];
  const payCols = [
    { key: 'wo_no', label: 'WO' }, { key: 'vendor_name', label: 'Vendor' }, { key: 'project_name', label: 'Project' },
    { key: 'retention_held_total', label: 'Held Total' }, { key: 'released', label: 'Released' },
    { key: 'balance', label: 'Balance' }, { key: 'dlp_end_date', label: 'DLP End' },
    { key: 'release_due', label: 'Release Due', value: (r) => (r.release_due ? 'YES' : '') },
  ];

  return (
    <div data-testid="retention-tab">
      <div className="flex items-center gap-3 mb-3">
        <DirToggle value={dir} onChange={setDir} testPrefix="retention-dir"
          options={[{ id: 'receivable', label: 'Receivable (clients hold on us)' }, { id: 'payable', label: 'Payable (we hold on subs)' }]} />
        <button className="ml-auto flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline"
          onClick={() => exportCSV(`retention-${dir}.csv`, dir === 'receivable' ? recvCols : payCols, rows)}
          data-testid="retention-export"><Download size={12} /> CSV</button>
      </div>
      {d && (
        <div className="flex flex-wrap gap-3 mb-3">
          <SummaryTile label="Balance" value={formatINR(d.summary?.balance_total)} tone="amber" testId="retention-balance-total" />
          {dir === 'payable' && <SummaryTile label="Due for Release" value={formatINR(d.summary?.due_for_release)} tone={d.summary?.due_for_release ? 'rose' : 'default'} testId="retention-due-release" />}
          {dir === 'receivable' && <SummaryTile label="Total Deducted" value={formatINR(d.summary?.deducted_total)} testId="retention-deducted-total" />}
        </div>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full">
          <thead className="bg-slate-50">
            {dir === 'receivable' ? (
              <tr><th className={TH}>Project</th><th className={TH}>Client</th><th className={THR}>Deducted</th><th className={THR}>Released to Us</th><th className={THR}>Balance</th><th className={TH}>Expected Release</th></tr>
            ) : (
              <tr><th className={TH}>WO</th><th className={TH}>Vendor</th><th className={TH}>Project</th><th className={THR}>Held Total</th><th className={THR}>Released</th><th className={THR}>Balance</th><th className={TH}>DLP End</th></tr>
            )}
          </thead>
          <tbody>
            {!d && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="retention-empty">No retention entries.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`retention-row-${i}`}>
                {dir === 'receivable' ? (<>
                  <td className={`${TD} font-bold`}>{r.project_name}</td>
                  <td className={TD}>{r.client_name}</td>
                  <td className={TDR}>{formatINR(r.retention_deducted)}</td>
                  <td className={`${TDR} text-emerald-700`}>{formatINR(r.released_to_us)}</td>
                  <td className={`${TDR} font-bold`}>{formatINR(r.balance)}</td>
                  <td className={TD}>{r.expected_release_date ? formatDate(r.expected_release_date) : '—'}</td>
                </>) : (<>
                  <td className={`${TD} font-bold`}>{r.wo_no}</td>
                  <td className={TD}>{r.vendor_name}</td>
                  <td className={TD}>{r.project_name}</td>
                  <td className={TDR}>{formatINR(r.retention_held_total)}</td>
                  <td className={`${TDR} text-emerald-700`}>{formatINR(r.released)}</td>
                  <td className={`${TDR} font-bold`}>{formatINR(r.balance)}</td>
                  <td className={TD}>
                    {r.dlp_end_date ? formatDate(r.dlp_end_date) : '—'}
                    {r.release_due && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm" data-testid={`release-due-${r.wo_no}`}>due</span>}
                  </td>
                </>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Advances tab ────────────────────────────────────────────────────────────
function AdvancesTab({ projectId }) {
  const [dir, setDir] = useState('given');
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    apiGet('/finance/advances-ledger', { direction: dir, ...(projectId ? { project_id: projectId } : {}) })
      .then(setD).catch(() => toast.error('Failed to load advances ledger'));
  }, [dir, projectId]);
  const rows = d?.rows || [];
  const cols = dir === 'given'
    ? [{ key: 'wo_no', label: 'WO' }, { key: 'vendor_name', label: 'Vendor' }, { key: 'project_name', label: 'Project' },
       { key: 'advance', label: 'Advance' }, { key: 'recovered', label: 'Recovered' }, { key: 'balance', label: 'Balance' },
       { key: 'voucher_paid', label: 'Voucher Paid' }, { key: 'voucher_gap', label: 'Voucher Gap', value: (r) => (r.voucher_gap ? 'YES' : '') }]
    : [{ key: 'project_name', label: 'Project' }, { key: 'client_name', label: 'Client' },
       { key: 'received', label: 'Received' }, { key: 'recovered', label: 'Recovered via RA' }, { key: 'balance', label: 'Balance' }];

  return (
    <div data-testid="advances-tab">
      <div className="flex items-center gap-3 mb-3">
        <DirToggle value={dir} onChange={setDir} testPrefix="advances-dir"
          options={[{ id: 'given', label: 'Given (to subs)' }, { id: 'received', label: 'Received (from clients)' }]} />
        <button className="ml-auto flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline"
          onClick={() => exportCSV(`advances-${dir}.csv`, cols, rows)} data-testid="advances-export"><Download size={12} /> CSV</button>
      </div>
      {d && (
        <div className="flex flex-wrap gap-3 mb-3">
          <SummaryTile label="Balance Outstanding" value={formatINR(d.summary?.balance_total)} tone="amber" testId="advances-balance-total" />
          {dir === 'given' && d.summary?.voucher_gaps > 0 && (
            <SummaryTile label="Voucher Gaps" value={d.summary.voucher_gaps} tone="rose" testId="advances-voucher-gaps" />
          )}
        </div>
      )}
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full">
          <thead className="bg-slate-50">
            {dir === 'given' ? (
              <tr><th className={TH}>WO</th><th className={TH}>Vendor</th><th className={TH}>Project</th><th className={THR}>Advance</th><th className={THR}>Recovered</th><th className={THR}>Balance</th><th className={THR}>Voucher Paid</th><th className={TH}>Gap</th></tr>
            ) : (
              <tr><th className={TH}>Project</th><th className={TH}>Client</th><th className={THR}>Received</th><th className={THR}>Recovered via RA</th><th className={THR}>Balance</th></tr>
            )}
          </thead>
          <tbody>
            {!d && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500" data-testid="advances-empty">No advances.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`advances-row-${i}`}>
                {dir === 'given' ? (<>
                  <td className={`${TD} font-bold`}>{r.wo_no}</td>
                  <td className={TD}>{r.vendor_name}</td>
                  <td className={TD}>{r.project_name}</td>
                  <td className={TDR}>{formatINR(r.advance)}</td>
                  <td className={`${TDR} text-emerald-700`}>{formatINR(r.recovered)}</td>
                  <td className={`${TDR} font-bold`}>{formatINR(r.balance)}</td>
                  <td className={TDR}>{formatINR(r.voucher_paid)}</td>
                  <td className={TD}>
                    {r.voucher_gap && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-rose-600" title="Advance on WO ≠ approved advance vouchers to this vendor" data-testid={`voucher-gap-${r.wo_no}`}>
                        <AlertTriangle size={11} /> gap
                      </span>
                    )}
                  </td>
                </>) : (<>
                  <td className={`${TD} font-bold`}>{r.project_name}</td>
                  <td className={TD}>{r.client_name}</td>
                  <td className={TDR}>{formatINR(r.received)}</td>
                  <td className={`${TDR} text-emerald-700`}>{formatINR(r.recovered)}</td>
                  <td className={`${TDR} font-bold`}>{formatINR(r.balance)}</td>
                </>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Unbilled WIP tab ────────────────────────────────────────────────────────
function WIPTab({ projectId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    apiGet('/finance/unbilled-wip', projectId ? { project_id: projectId } : {})
      .then(setD).catch(() => toast.error('Failed to load unbilled WIP'));
  }, [projectId]);
  const rows = d?.rows || [];
  const cols = [
    { key: 'project_name', label: 'Project' }, { key: 'client_name', label: 'Client' },
    { key: 'work_done_value', label: 'Work Done' }, { key: 'billed_value', label: 'Billed' },
    { key: 'collected_value', label: 'Collected' }, { key: 'unbilled_wip', label: 'Unbilled WIP' },
    { key: 'uncollected', label: 'Uncollected' },
    { key: 'no_boq_basis', label: 'No BOQ Basis', value: (r) => (r.no_boq_basis ? 'YES' : '') },
  ];

  // 3-stage funnel: collected | billed-but-uncollected | unbilled work
  const Funnel = ({ r }) => {
    if (r.no_boq_basis) return <div className="text-[10px] text-slate-400 italic">no BOQ basis</div>;
    const work = Math.max(r.work_done_value || 0, r.billed_value || 0, 1);
    const seg = (v) => `${Math.max(0, Math.min(100, (v / work) * 100))}%`;
    return (
      <div className="w-44">
        <div className="flex h-3.5 rounded-sm overflow-hidden bg-slate-100 border border-slate-200">
          <div className="bg-emerald-500" style={{ width: seg(r.collected_value) }} title={`Collected ${formatINR(r.collected_value)}`} />
          <div className="bg-amber-400" style={{ width: seg(r.billed_value - r.collected_value) }} title={`Billed, uncollected ${formatINR(r.uncollected)}`} />
          {r.unbilled_wip > 0 && <div className="bg-slate-400" style={{ width: seg(r.unbilled_wip) }} title={`Unbilled ${formatINR(r.unbilled_wip)}`} />}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="wip-tab">
      {d && (
        <div className="flex flex-wrap gap-3 mb-3">
          <SummaryTile label="Contract Asset (unbilled)" value={formatINR(d.summary?.contract_asset_total)} tone="amber" testId="wip-contract-asset" />
          <SummaryTile label="Over-billed" value={formatINR(d.summary?.over_billed_total)} tone={d.summary?.over_billed_total ? 'rose' : 'default'} testId="wip-over-billed" />
          <SummaryTile label="Billed, Uncollected" value={formatINR(d.summary?.uncollected_total)} testId="wip-uncollected" />
        </div>
      )}
      <div className="flex justify-end mb-2">
        <button className="flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline"
          onClick={() => exportCSV('unbilled-wip.csv', cols, rows)} data-testid="wip-export"><Download size={12} /> CSV</button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr><th className={TH}>Project</th><th className={THR}>Work Done</th><th className={THR}>Billed</th><th className={THR}>Collected</th><th className={THR}>Unbilled WIP</th><th className={THR}>Uncollected</th><th className={TH}>Pipeline</th></tr>
          </thead>
          <tbody>
            {!d && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="wip-empty">No projects with BOQ or client bills.</td></tr>}
            {rows.map((r) => (
              <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`wip-row-${r.project_id}`}>
                <td className={`${TD} font-bold`}>
                  {r.project_name}
                  {r.no_boq_basis && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded-sm" data-testid={`no-boq-${r.project_id}`}>no BOQ basis</span>}
                  {r.over_billed && <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm" data-testid={`over-billed-${r.project_id}`}>over-billed</span>}
                </td>
                <td className={TDR}>{r.work_done_value === null ? '—' : formatINR(r.work_done_value)}</td>
                <td className={TDR}>{formatINR(r.billed_value)}</td>
                <td className={`${TDR} text-emerald-700`}>{formatINR(r.collected_value)}</td>
                <td className={`${TDR} font-bold ${r.unbilled_wip < 0 ? 'text-rose-600' : ''}`}>{r.unbilled_wip === null ? '—' : formatINR(r.unbilled_wip)}</td>
                <td className={`${TDR} ${r.uncollected > 0 ? 'text-amber-700' : ''}`}>{formatINR(r.uncollected)}</td>
                <td className={TD}><Funnel r={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 inline-block rounded-sm" /> Collected</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 inline-block rounded-sm" /> Billed, uncollected</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-400 inline-block rounded-sm" /> Unbilled work done</span>
      </div>
    </div>
  );
}

// ─── Client TDS tab ──────────────────────────────────────────────────────────
function ClientTDSTab({ projectId }) {
  const fyNow = (() => { const t = new Date(); const sy = t.getMonth() + 1 >= 4 ? t.getFullYear() : t.getFullYear() - 1; return `${sy}-${String(sy + 1).slice(2)}`; })();
  const [fy, setFy] = useState(fyNow);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    setD(null);
    apiGet('/finance/client-tds', { fy, ...(projectId ? { project_id: projectId } : {}) })
      .then(setD).catch(() => toast.error('Failed to load client TDS'));
  }, [fy, projectId]);
  useEffect(() => { load(); }, [load]);

  const fyOptions = useMemo(() => {
    const sy = Number(fyNow.split('-')[0]);
    return [sy - 2, sy - 1, sy, sy + 1].map((y) => `${y}-${String(y + 1).slice(2)}`);
  }, [fyNow]);

  const toggleMatched = async (rc) => {
    try {
      await apiPost(`/finance/client-tds/${rc.receipt_id}/mark-matched`, { matched: !rc.matched_26as });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update'); }
  };

  const rows = d?.rows || [];
  return (
    <div data-testid="client-tds-tab">
      <div className="flex items-center gap-3 mb-3">
        <select className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs" value={fy} onChange={(e) => setFy(e.target.value)} data-testid="tds-fy-select">
          {fyOptions.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        {d && <SummaryTile label="Total Client TDS (26AS asset)" value={formatINR(d.summary?.total_tds)} tone="amber" testId="tds-total" />}
        <button className="ml-auto flex items-center gap-1 text-xs font-bold text-[#3b4872] hover:underline"
          onClick={() => exportCSV(`client-tds-${fy}.csv`,
            [{ key: 'client_name', label: 'Client' }, { key: 'tan', label: 'TAN' }, { key: 'q1', label: 'Q1' }, { key: 'q2', label: 'Q2' }, { key: 'q3', label: 'Q3' }, { key: 'q4', label: 'Q4' }, { key: 'total', label: 'Total' }], rows)}
          data-testid="tds-export"><Download size={12} /> CSV</button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr><th className={TH}>Client</th><th className={TH}>TAN</th><th className={THR}>Q1</th><th className={THR}>Q2</th><th className={THR}>Q3</th><th className={THR}>Q4</th><th className={THR}>Total</th><th className={TH}>Receipts</th></tr>
          </thead>
          <tbody>
            {!d && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {d && rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500" data-testid="tds-empty">No client TDS recorded in FY {fy}. TDS is captured on receipt vouchers.</td></tr>}
            {rows.map((g) => (
              <React.Fragment key={g.client_name}>
                <tr className="border-t border-slate-100 bg-slate-50/50" data-testid={`tds-row-${g.client_name}`}>
                  <td className={`${TD} font-bold`}>{g.client_name}</td>
                  <td className={TD}>{g.tan || '—'}</td>
                  <td className={TDR}>{formatINR(g.q1)}</td>
                  <td className={TDR}>{formatINR(g.q2)}</td>
                  <td className={TDR}>{formatINR(g.q3)}</td>
                  <td className={TDR}>{formatINR(g.q4)}</td>
                  <td className={`${TDR} font-bold`}>{formatINR(g.total)}</td>
                  <td className={TD}>{g.receipts.length}</td>
                </tr>
                {g.receipts.map((rc) => (
                  <tr key={rc.receipt_id} className="border-t border-slate-50 text-xs" data-testid={`tds-receipt-${rc.voucher_no}`}>
                    <td className="px-3 py-1 pl-8 text-slate-500" colSpan={2}>{rc.voucher_no} · {formatDate(rc.receipt_date)} · {rc.quarter}{rc.project_name ? ` · ${rc.project_name}` : ''}</td>
                    <td colSpan={4} className="px-3 py-1 text-right text-slate-500">gross {formatINR(rc.gross_receipt)}</td>
                    <td className="px-3 py-1 text-right font-bold">{formatINR(rc.tds)}</td>
                    <td className="px-3 py-1">
                      <button onClick={() => toggleMatched(rc)} className={`flex items-center gap-1 text-[10px] font-bold ${rc.matched_26as ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`tds-match-${rc.voucher_no}`}>
                        {rc.matched_26as ? <CheckCircle2 size={12} /> : <Circle size={12} />} 26AS
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function EPCLedgers() {
  const [tab, setTab] = useUrlTab('tab', 'retention');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  useEffect(() => { apiGet('/projects').then((d) => setProjects(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const TABS = [
    { id: 'retention', label: 'Retention', icon: Scale },
    { id: 'advances', label: 'Advances', icon: ArrowLeftRight },
    { id: 'wip', label: 'Unbilled WIP', icon: Layers },
    { id: 'tds', label: 'Client TDS', icon: FileSpreadsheet },
  ];

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="epc-ledgers-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Scale size={26} className="text-[#3b4872]" /> EPC Ledgers</h1>
          <p className="text-sm text-slate-600 mt-1">Retention (both directions) · Advances · Unbilled WIP funnel · Client TDS receivable — derived live from source documents.</p>
        </div>
        <select className="border border-slate-300 rounded-sm px-3 py-2 text-sm" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="ledger-project-filter">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="flex border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'retention' && <RetentionTab projectId={projectId} />}
      {tab === 'advances' && <AdvancesTab projectId={projectId} />}
      {tab === 'wip' && <WIPTab projectId={projectId} />}
      {tab === 'tds' && <ClientTDSTab projectId={projectId} />}
    </div>
  );
}
